import React, { useState, useEffect } from 'react';
import RelationshipVisualization from './RelationshipVisualization';
import { API } from '../../../../common/Constants';
import { getAPIHeaders } from '../../../../common/Utils';
import useFetch from '../../../../hooks/useFetch';

interface RelationshipVisualizerProps {
  curriculumId?: number;
  courseId?: number;
  subjectId?: number;
  syllabusId?: number;
  showFullHierarchy?: boolean;
}

const RelationshipVisualizer: React.FC<RelationshipVisualizerProps> = ({
  curriculumId,
  courseId,
  subjectId,
  syllabusId,
  showFullHierarchy = false
}) => {
  const [relationshipData, setRelationshipData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isLoading, callFetch } = useFetch();

  useEffect(() => {
    const loadRelationshipData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const responseData = await callFetch(
          API.ACADEMIC_LENS,
          'GET',
          getAPIHeaders(),
          null,
          null
        );
        
        let filteredData;
        
        // Filter data based on provided IDs
        if (curriculumId) {
          filteredData = responseData.filter((curriculum: any) => curriculum.id === curriculumId);
        } else if (courseId) {
          // Filter to find curricula containing this course
          filteredData = responseData.filter((curriculum: any) => 
            curriculum.courses?.some((course: any) => course.id === courseId)
          );
        } else {
          // Use all data
          filteredData = responseData;
        }
        
        setRelationshipData(filteredData);
        
      } catch (err) {
        console.error('Error loading relationship data:', err);
        setError('Failed to load relationship data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadRelationshipData();
  }, [curriculumId, courseId, subjectId, syllabusId, showFullHierarchy]);

  return (
    <div className="relationship-visualizer-container">
      {loading && (
        <div className="text-center p-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading relationship diagram...</p>
        </div>
      )}
      
      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="ti ti-alert-circle me-2"></i>
          {error}
        </div>
      )}
      
      {!loading && !error && (
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="mb-0">
              <i className="ti ti-hierarchy me-2"></i>
              Academic Relationship Diagram
            </h5>
          </div>
          <div className="card-body">
            <div className="mb-3 text-muted small">
              <p>This diagram visualizes the hierarchical relationship between curriculum, courses, subjects, and syllabi in your academic structure.</p>
            </div>
            <RelationshipVisualization 
              data={relationshipData} 
              curriculumId={curriculumId}
              courseId={courseId}
              showFullHierarchy={showFullHierarchy}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RelationshipVisualizer;